import React from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faInfoCircle} from '@fortawesome/free-solid-svg-icons';
import {
  BoldIcon, ItalicIcon, ListBulletIcon, NumberedListIcon,
  CodeBracketIcon, CodeBracketSquareIcon,
} from '@heroicons/react-v2/24/outline';
import {
  useEditor, EditorContent,
  NodeViewWrapper, NodeViewContent, ReactNodeViewRenderer,
} from '@tiptap/react';
import {Extension} from '@tiptap/core';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import {Plugin, PluginKey} from '@tiptap/pm/state';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import {Markdown} from 'tiptap-markdown';
import {createLowlight} from 'lowlight';
import 'highlight.js/styles/xcode.css';

import bash from 'highlight.js/lib/languages/bash';
import c from 'highlight.js/lib/languages/c';
import cpp from 'highlight.js/lib/languages/cpp';
import css from 'highlight.js/lib/languages/css';
import go from 'highlight.js/lib/languages/go';
import xml from 'highlight.js/lib/languages/xml';
import java from 'highlight.js/lib/languages/java';
import javascript from 'highlight.js/lib/languages/javascript';
import kotlin from 'highlight.js/lib/languages/kotlin';
import perl from 'highlight.js/lib/languages/perl';
import php from 'highlight.js/lib/languages/php';
import python from 'highlight.js/lib/languages/python';
import ruby from 'highlight.js/lib/languages/ruby';
import rust from 'highlight.js/lib/languages/rust';
import scala from 'highlight.js/lib/languages/scala';
import sql from 'highlight.js/lib/languages/sql';
import swift from 'highlight.js/lib/languages/swift';

import Tooltip from './Tooltip';
import Button from './ButtonNew';

// Matches the language list from the previous draft-js editor. Keys are the
// markdown fence tags (```python …); values are dropdown labels. (highlight.js
// uses its `xml` grammar for HTML, and `javascript` for js.)
const LANGUAGES = {
  bash: 'Bash',
  c: 'C',
  cpp: 'C++',
  css: 'CSS',
  go: 'Go',
  html: 'HTML',
  java: 'Java',
  js: 'JavaScript',
  kotlin: 'Kotlin',
  perl: 'Perl',
  php: 'PHP',
  python: 'Python',
  ruby: 'Ruby',
  rust: 'Rust',
  scala: 'Scala',
  sql: 'SQL',
  swift: 'Swift',
};

const lowlight = createLowlight();
lowlight.register({
  bash,
  c,
  cpp,
  css,
  go,
  html: xml,
  java,
  js: javascript,
  kotlin,
  perl,
  php,
  python,
  ruby,
  rust,
  scala,
  sql,
  swift,
});

// Code block rendered with a per-block language <select>. The chosen language
// is stored on the node's `language` attr and serialized into the markdown
// fence by tiptap-markdown (```python), so it persists and drives display too.
const CodeBlockView = ({node, updateAttributes}) => (
  <NodeViewWrapper className="not-prose bg-white rounded-xl border border-gray-200 group my-5 relative">
    <div className="px-4 py-2 border-b border-gray-200 flex items-center justify-between text-sm font-medium text-gray-600">
      <select
        contentEditable={false}
        value={node.attrs.language || ''}
        onChange={(e) => updateAttributes({language: e.target.value})}
        className="text-xs bg-white border border-gray-300 rounded px-1 py-0.5 text-gray-600"
      >
        <option value="">plain</option>
        {Object.entries(LANGUAGES).map(([value, label]) => (
          <option key={value} value={value}>{label}</option>
        ))}
      </select>
    </div>
    <pre className="px-6 py-4 bg-transparent! overflow-x-auto">
      <NodeViewContent as="code" className="text-sm" />
    </pre>
  </NodeViewWrapper>
);

const CodeBlockWithLanguage = CodeBlockLowlight
  .extend({addNodeView: () => ReactNodeViewRenderer(CodeBlockView)})
  .configure({lowlight});

// A code block as the last node traps the cursor (Enter only adds code lines).
// Guarantee an empty paragraph always follows a trailing code block so the user
// can move past it (arrow down / click) and keep typing normal text.
const TrailingNode = Extension.create({
  name: 'trailingParagraphAfterCodeBlock',
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('trailingParagraphAfterCodeBlock'),
        appendTransaction: (transactions, oldState, newState) => {
          if (!transactions.some((tr) => tr.docChanged)) return null;
          const {doc, tr, schema} = newState;
          const last = doc.lastChild;
          if (last && last.type.name === 'codeBlock' && schema.nodes.paragraph) {
            return tr.insert(doc.content.size, schema.nodes.paragraph.create());
          }
          return null;
        },
      }),
    ];
  },
});

// Heroicons has no blockquote glyph; drawn in the same stroke style so it sits
// naturally alongside BoldIcon/ItalicIcon/etc, echoing the border-left + text
// look of a rendered blockquote.
const QuoteIcon = ({className}) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5.25 4.5v15" />
    <path d="M9 8.25h9.75" />
    <path d="M9 12h9.75" />
    <path d="M9 15.75h6" />
  </svg>
);

const ToolbarButton = ({
  onClick, label, active = false, children,
}) => (
  <button
    type="button"
    aria-label={label}
    aria-pressed={active}
    title={label}
    className={`p-1.5 mr-1 rounded ${
      active ? 'bg-gray-200 text-gray-900' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
    }`}
    onClick={onClick}
  >
    {children}
  </button>
);

const ToolbarDivider = () => <span className="w-px h-4 bg-gray-200 mx-1.5" aria-hidden />;

export const Toolbar = ({editor}) => {
  if (!editor) return null;
  const chain = () => editor.chain().focus();
  return (
    <div className="hidden md:flex items-center">
      <ToolbarButton label="Bold" active={editor.isActive('bold')} onClick={() => chain().toggleBold().run()}>
        <BoldIcon className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton label="Italic" active={editor.isActive('italic')} onClick={() => chain().toggleItalic().run()}>
        <ItalicIcon className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarDivider />
      <ToolbarButton label="Bulleted list" active={editor.isActive('bulletList')} onClick={() => chain().toggleBulletList().run()}>
        <ListBulletIcon className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton label="Numbered list" active={editor.isActive('orderedList')} onClick={() => chain().toggleOrderedList().run()}>
        <NumberedListIcon className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton label="Quote" active={editor.isActive('blockquote')} onClick={() => chain().toggleBlockquote().run()}>
        <QuoteIcon className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarDivider />
      <ToolbarButton label="Inline code" active={editor.isActive('code')} onClick={() => chain().toggleCode().run()}>
        <CodeBracketIcon className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton label="Code block" active={editor.isActive('codeBlock')} onClick={() => chain().toggleCodeBlock().run()}>
        <CodeBracketSquareIcon className="h-4 w-4" />
      </ToolbarButton>
      <Tooltip content="Markdown formatting and shortcuts supported">
        <div className="ml-2">
          <FontAwesomeIcon className="icon-edit" icon={faInfoCircle} />
        </div>
      </Tooltip>
    </div>
  );
};

export const buildExtensions = (placeholderText) => [
  // Use our lowlight-backed code block (with language dropdown) instead.
  StarterKit.configure({codeBlock: false}),
  CodeBlockWithLanguage,
  TrailingNode,
  Placeholder.configure({placeholder: placeholderText}),
  // Parses the initial `content` markdown and serializes back to markdown.
  Markdown.configure({html: false, transformPastedText: true}),
];

export const EDITOR_CONTENT_CLASS = 'prose prose-sm max-w-none focus:outline-none min-h-[3rem] prose-code:text-sm prose-pre:text-sm';

const RichTextEditor = ({
  initialMarkdown,
  onChange,
  showToolbar,
  placeholderText,
  submitting,
  onCancel,
  onSubmit,
}) => {
  const editor = useEditor({
    // Required for Next SSR; the editor renders on the client.
    immediatelyRender: false,
    autofocus: 'end',
    extensions: buildExtensions(placeholderText),
    content: initialMarkdown || '',
    onUpdate: ({editor: ed}) => onChange(ed.storage.markdown.getMarkdown()),
    editorProps: {
      attributes: {
        class: EDITOR_CONTENT_CLASS,
      },
    },
  });

  return (
    <>
      {/* flex-1 only takes effect when a flex-column ancestor (e.g. AnswerEditorPanel)
          has extra height to give it; harmless in the default (non-flex) usages. */}
      <div className="p-5 text-base flex-1 overflow-y-auto">
        <EditorContent editor={editor} />
      </div>
      <div className="flex items-center justify-between p-2 flex-shrink-0">
        <div className="flex items-center">
          {showToolbar && <Toolbar editor={editor} />}
        </div>
        <div className="flex items-center">
          {onCancel && (
            <button className="p-2 cursor-pointer mr-4 font-medium" type="button" onClick={onCancel}>Cancel</button>
          )}
          <span>
            <Button disabled={submitting} onClick={onSubmit}>
              {submitting ? 'Submitting...' : 'Submit'}
            </Button>
          </span>
        </div>
      </div>
    </>
  );
};

export default RichTextEditor;
